import SearchableSelect from '../SearchableSelect';
import { tokenNameOf } from './bannerTokens';
import type { BannerToken } from './bannerApi';

const CHIP =
  'rounded-full border border-primary-200 bg-primary-50 px-2.5 py-0.5 text-xs font-medium text-primary-700 hover:bg-primary-100';
const MORE =
  'flex items-center gap-1 rounded-full border border-gray-300 bg-white px-2.5 py-0.5 text-xs font-medium text-gray-700 hover:bg-gray-50 [&>svg]:h-3 [&>svg]:w-3';

/** The chips on show: first name and store, this task's figures, then the next detail there is. */
const pickChips = (tokens: BannerToken[]): BannerToken[] => {
  const named = (name: string) => tokens.filter((t) => tokenNameOf(t.token) === name);
  const figures = tokens.filter((t) => t.figureOnly);
  const lead = [...named('person.first_name'), ...named('store.name')];
  const taken = new Set([...lead, ...figures]);
  const next = tokens.filter((t) => !taken.has(t) && (t.group === 'Store' || t.group === 'Employee')).slice(0, 1);
  return [...lead, ...(figures.length ? figures : next)];
};

/** "Add a detail" under the message: puts each person's own detail into the text at the cursor. */
export function TokenChips({
  tokens,
  onInsert,
}: {
  /** Those this send can fill. */
  tokens: BannerToken[];
  onInsert: (token: string) => void;
}) {
  if (tokens.length === 0) return null;
  const chips = pickChips(tokens);
  const rest = tokens.filter((t) => !chips.includes(t));
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-gray-500">Add a detail</span>
      {chips.map((t) => (
        <button key={t.token} type="button" onClick={() => onInsert(t.token)} className={CHIP}>
          {t.label}
        </button>
      ))}
      {rest.length > 0 && (
        <div>
          <label htmlFor="banner-more-details" className="sr-only">
            More details
          </label>
          <SearchableSelect
            id="banner-more-details"
            value=""
            onChange={(token) => token && onInsert(token)}
            options={rest.map((t) => ({ value: t.token, label: t.label, group: t.group }))}
            placeholder="More"
            searchPlaceholder="Search details…"
            allowClear={false}
            triggerClassName={MORE}
            minPanelWidth={240}
          />
        </div>
      )}
    </div>
  );
}
